/**
 * FERP Portal - Calendar Page (DRR-115)
 *
 * Production-quality task calendar showing all tasks across all workflows.
 * Part of the Board → Gantt → Calendar view trinity.
 *
 * Features:
 * - StatCard widgets matching WorkflowStateView
 * - Cross-view navigation (Board View, Gantt View)
 * - Smart search filter (name + description)
 * - TaskDetailPanel integration (click event → inline detail)
 * - Priority borders + event tooltips
 * - FullCalendar with business hours + slot styling
 * - Status legend with icons
 *
 * FERP Principles:
 * - Dumb Frontend: All data from backend Task primitives
 * - NO hardcoded values: Status/priority from generated enums
 * - Repository pattern: All data through generated Apollo hooks
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Container,
  Group,
  Stack,
  Text,
  Title,
  Alert,
  Loader,
  ThemeIcon,
  SegmentedControl,
  Paper,
  Box,
  Switch,
  Card,
  Button,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconCalendar,
  IconAlertTriangle,
  IconAlertOctagon,
  IconCheckbox,
  IconActivity,
  IconLayoutColumns,
  IconTimeline,
  IconRefresh,
  IconSearch,
  IconClockHour4,
  IconPlayerPlay,
  IconUserCheck,
  IconLoader as IconLoaderTab,
  IconPlayerPause,
  IconExternalLink,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
// NOTE: @fullcalendar/rrule requires npm install - skipping for now
// import rrulePlugin from '@fullcalendar/rrule';
import type { EventClickArg, EventContentArg, EventInput, EventDropArg } from '@fullcalendar/core';
import { useNavigate } from 'react-router-dom';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  useListTasksQuery,
  GqlTaskStatus,
  GqlTaskPriority,
  type TaskFieldsFragment,
  useRescheduleTaskMutation,
} from '@/generated/graphql';
import { useDevActorId } from '@/hooks/useDevActorId';
import {
  getTaskStatusColor,
  TASK_STATUS_LEGEND,
} from '@/pages/workflow-state/task-status-colors';
import { TaskDetailPanel } from '@/pages/workflow-state/TaskDetailPanel';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Default duration when no estimate is available (1 hour in ms) */
const DEFAULT_DURATION_MS = 3_600_000;

type FilterMode = 'mine' | 'all';

/** Status → icon mapping (mirrors WorkflowStateView COLUMN_ICONS) */
const STATUS_ICONS: Record<string, React.ReactNode> = {
  [GqlTaskStatus.PENDING]: <IconClockHour4 size={12} />,
  [GqlTaskStatus.READY]: <IconPlayerPlay size={12} />,
  [GqlTaskStatus.ASSIGNED]: <IconUserCheck size={12} />,
  [GqlTaskStatus.IN_PROGRESS]: <IconLoaderTab size={12} />,
  [GqlTaskStatus.BLOCKED]: <IconAlertTriangle size={12} />,
  [GqlTaskStatus.PAUSED]: <IconPlayerPause size={12} />,
  [GqlTaskStatus.PENDING_EXTERNAL]: <IconExternalLink size={12} />,
  [GqlTaskStatus.COMPLETED]: <IconCheck size={12} />,
  [GqlTaskStatus.FAILED]: <IconX size={12} />,
};

// ============================================================================
// STAT CARD (matches WorkflowStateView pattern)
// ============================================================================

function StatCard({ label, value, color, icon }: {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <Card withBorder p="xs" style={{ flex: 1, minWidth: 100 }}>
      <Group gap="xs" wrap="nowrap">
        <ThemeIcon variant="light" size="sm" color={color}>{icon}</ThemeIcon>
        <div>
          <Text size="xs" c="dimmed">{label}</Text>
          <Text size="lg" fw={700}>{value}</Text>
        </div>
      </Group>
    </Card>
  );
}

// ============================================================================
// EVENT CONVERSION
// ============================================================================

interface TaskItem {
  id: string;
  name: string;
  status: string;
  priority: string;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  dueAt?: string | null;
  estimatedDurationMs?: string | null;
  assignedActorId?: string | null;
  workflowInstanceId: string;
  description: string;
  progressPercent?: number | null;
}

/**
 * Convert task to FullCalendar event(s).
 * Priority order (DRR-116):
 *   1. scheduledStart/scheduledEnd (planned schedule)
 *   2. startedAt/completedAt (actual execution)
 *   3. startedAt + estimatedDurationMs (in-progress)
 *   4. createdAt + estimatedDurationMs (last resort)
 * Also returns a deadline marker if dueAt is set.
 */
function taskToEvents(task: TaskItem): EventInput[] {
  const colors = getTaskStatusColor(task.status);
  const estimatedMs = task.estimatedDurationMs
    ? parseInt(task.estimatedDurationMs, 10)
    : DEFAULT_DURATION_MS;

  let start: Date;
  let end: Date;

  if (task.scheduledStart && task.scheduledEnd) {
    start = new Date(task.scheduledStart);
    end = new Date(task.scheduledEnd);
  } else if (task.scheduledStart) {
    start = new Date(task.scheduledStart);
    end = new Date(start.getTime() + estimatedMs);
  } else if (task.startedAt && task.completedAt) {
    start = new Date(task.startedAt);
    end = new Date(task.completedAt);
  } else if (task.startedAt) {
    start = new Date(task.startedAt);
    end = new Date(start.getTime() + estimatedMs);
  } else {
    start = new Date(task.createdAt);
    end = new Date(start.getTime() + estimatedMs);
  }

  const isHighPriority =
    task.priority === GqlTaskPriority.CRITICAL || task.priority === GqlTaskPriority.HIGH;

  const priorityColor = isHighPriority
    ? (task.priority === GqlTaskPriority.CRITICAL ? '#c92a2a' : '#e8590c')
    : undefined;

  const events: EventInput[] = [{
    id: task.id,
    title: `${isHighPriority ? '! ' : ''}${task.name}`,
    start: start.toISOString(),
    end: end.toISOString(),
    backgroundColor: task.status === GqlTaskStatus.CANCELLED ? `${colors.bg}80` : colors.bg,
    borderColor: colors.border,
    textColor: '#fff',
    extendedProps: {
      status: task.status,
      priority: task.priority,
      description: task.description,
      workflowInstanceId: task.workflowInstanceId,
      priorityColor,
      progressPercent: task.progressPercent,
      dueAt: task.dueAt,
    },
  }];

  // Add deadline marker if dueAt is set
  if (task.dueAt) {
    events.push({
      id: `${task.id}-deadline`,
      title: `\u23F0 ${task.name} — deadline`,
      start: task.dueAt,
      allDay: false,
      display: 'background',
      backgroundColor: 'rgba(201, 42, 42, 0.15)',
      borderColor: '#c92a2a',
    });
  }

  return events;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function CalendarPage() {
  const navigate = useNavigate();
  const { actorId } = useDevActorId();
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [showCompleted, setShowCompleted] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTask, setSelectedTask] = useState<TaskFieldsFragment | null>(null);
  const [panelOpened, setPanelOpened] = useState(false);

  const { data, loading, error, refetch } = useListTasksQuery({
    variables: { limit: 500 },
  });

  const tasks = useMemo(() => {
    const items = (data?.taskApiListTasks?.items ?? []) as TaskFieldsFragment[];
    return items.filter(task => {
      if (filterMode === 'mine' && actorId && task.assignedActor?.id !== actorId) {
        return false;
      }
      if (!showCompleted && task.status === GqlTaskStatus.COMPLETED) {
        return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const nameMatch = task.name.toLowerCase().includes(q);
        const descMatch = task.description?.toLowerCase().includes(q);
        if (!nameMatch && !descMatch) return false;
      }
      return true;
    });
  }, [data, filterMode, actorId, showCompleted, searchQuery]);

  const events = useMemo<EventInput[]>(
    () => tasks.flatMap(t => taskToEvents(t as unknown as TaskItem)),
    [tasks],
  );

  // Stats (matches WorkflowStateView pattern)
  const stats = useMemo(() => {
    const completed = tasks.filter(t => t.status === GqlTaskStatus.COMPLETED).length;
    const active = tasks.filter(t => t.status === GqlTaskStatus.IN_PROGRESS).length;
    const blocked = tasks.filter(t => t.status === GqlTaskStatus.BLOCKED).length;
    const failed = tasks.filter(t => t.status === GqlTaskStatus.FAILED).length;
    return { total: tasks.length, completed, active, blocked, failed };
  }, [tasks]);

  const handleEventClick = useCallback((info: EventClickArg) => {
    const clickedTaskId = info.event.id;
    const task = tasks.find(t => t.id === clickedTaskId);
    if (task) {
      setSelectedTask(task as TaskFieldsFragment);
      setPanelOpened(true);
    }
  }, [tasks]);

  // Reschedule mutation for drag-and-drop
  const [rescheduleTask] = useRescheduleTaskMutation();

  const handleEventDrop = useCallback(async (info: EventDropArg) => {
    const taskId = info.event.id;
    const task = tasks.find(t => t.id === taskId);
    if (!task) {
      info.revert();
      return;
    }

    const newStart = info.event.start;
    const newEnd = info.event.end || new Date(newStart!.getTime() + DEFAULT_DURATION_MS);

    // Life-critical safety check
    const extProps = info.event.extendedProps;
    const isLifeCritical = extProps?.priority === GqlTaskPriority.CRITICAL;

    const performReschedule = async (lifeCriticalOverride = false) => {
      try {
        await rescheduleTask({
          variables: {
            input: {
              taskId,
              scheduledStart: newStart!.toISOString(),
              scheduledEnd: newEnd.toISOString(),
              lifeCriticalOverride: lifeCriticalOverride || undefined,
            },
          },
        });
        notifications.show({
          title: 'Task Rescheduled',
          message: `${task.name} moved to ${newStart!.toLocaleString()}`,
          color: 'green',
        });
        refetch();
      } catch (err) {
        notifications.show({
          title: 'Reschedule Failed',
          message: err instanceof Error ? err.message : 'Unknown error',
          color: 'red',
        });
        info.revert();
      }
    };

    if (isLifeCritical) {
      modals.openConfirmModal({
        title: 'Reschedule Life-Critical Task?',
        children: (
          <Text size="sm">
            This task is marked as <strong>CRITICAL</strong>. Rescheduling may affect
            patient safety or regulatory compliance. Are you sure?
          </Text>
        ),
        labels: { confirm: 'Yes, Reschedule', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => performReschedule(true),
        onCancel: () => info.revert(),
      });
    } else {
      performReschedule();
    }
  }, [tasks, rescheduleTask, refetch]);

  if (error) {
    return (
      <Container fluid py="md" px="md">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Tasks">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid py="md" px="md">
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            <ThemeIcon variant="light" size="lg" color="blue">
              <IconCalendar size={20} />
            </ThemeIcon>
            <div>
              <Title order={3}>Calendar</Title>
              <Text size="xs" c="dimmed">
                Task schedule across all workflows
                {tasks.length > 0 && ` \u2022 ${tasks.length} tasks`}
              </Text>
            </div>
          </Group>

          <Group gap="sm">
            <Button
              leftSection={<IconLayoutColumns size={14} />}
              variant="subtle"
              size="xs"
              onClick={() => navigate('/ferp-core/workflow-state')}
            >
              Board View
            </Button>
            <Button
              leftSection={<IconTimeline size={14} />}
              variant="subtle"
              size="xs"
              onClick={() => navigate('/ferp-core/gantt')}
            >
              Gantt View
            </Button>
            <SegmentedControl
              size="xs"
              value={filterMode}
              onChange={(v) => setFilterMode(v as FilterMode)}
              data={[
                { label: 'All Tasks', value: 'all' },
                { label: 'My Tasks', value: 'mine' },
              ]}
            />
            <Switch
              label="Completed"
              size="xs"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.currentTarget.checked)}
            />
            <Button
              leftSection={<IconRefresh size={14} />}
              variant="light"
              onClick={() => refetch()}
              loading={loading}
              size="xs"
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Stats row */}
        <Group gap="xs" wrap="wrap">
          <StatCard label="Active" value={stats.active} color="cyan" icon={<IconActivity size={14} />} />
          <StatCard label="Blocked" value={stats.blocked} color="orange" icon={<IconAlertOctagon size={14} />} />
          <StatCard label="Completed" value={stats.completed} color="green" icon={<IconCheckbox size={14} />} />
          <StatCard label="Failed" value={stats.failed} color="red" icon={<IconAlertTriangle size={14} />} />
        </Group>

        {/* Search */}
        <TextInput
          placeholder="Filter tasks by name or description..."
          leftSection={<IconSearch size={14} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          size="xs"
        />

        {/* Calendar */}
        {loading ? (
          <Group justify="center" py="xl"><Loader /></Group>
        ) : (
          <Paper shadow="xs" radius="sm" withBorder p="md">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
              }}
              events={events}
              eventClick={handleEventClick}
              eventDrop={handleEventDrop}
              editable={true}
              eventDurationEditable={false}
              selectable={false}
              dayMaxEvents={4}
              weekends={true}
              height="auto"
              nowIndicator={true}
              eventDisplay="block"
              eventContent={renderTaskEvent}
              dayHeaderFormat={{ weekday: 'short', month: 'numeric', day: 'numeric' }}
              slotMinTime="06:00:00"
              slotMaxTime="22:00:00"
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5],
                startTime: '08:00',
                endTime: '18:00',
              }}
              slotDuration="00:30:00"
              expandRows={true}
              eventMaxStack={3}
            />
          </Paper>
        )}

        {/* Legend with status icons */}
        <Group gap="xs" wrap="wrap">
          <Text size="xs" c="dimmed" fw={500}>Legend:</Text>
          {TASK_STATUS_LEGEND.map(entry => (
            <Group key={entry.label} gap={4}>
              <Box
                w={10}
                h={10}
                style={{ backgroundColor: entry.color, borderRadius: 2, flexShrink: 0 }}
              />
              {STATUS_ICONS[entry.label.toUpperCase().replace(/ /g, '_')] && (
                <Box style={{ opacity: 0.6 }}>
                  {STATUS_ICONS[entry.label.toUpperCase().replace(/ /g, '_')]}
                </Box>
              )}
              <Text size="xs" c="dimmed">{entry.label}</Text>
            </Group>
          ))}
        </Group>
      </Stack>

      {/* Task Detail Panel — inline on event click */}
      <TaskDetailPanel
        selectedTask={selectedTask}
        opened={panelOpened}
        onClose={() => {
          setPanelOpened(false);
          setSelectedTask(null);
        }}
        allTasks={tasks as TaskFieldsFragment[]}
        onRefetch={() => refetch()}
        onTaskSelect={(task) => setSelectedTask(task)}
      />
    </Container>
  );
}

// ============================================================================
// CUSTOM EVENT RENDERER
// ============================================================================

function renderTaskEvent(eventInfo: EventContentArg) {
  const { status, priority, description, priorityColor, progressPercent, dueAt } = eventInfo.event.extendedProps;
  const isHighPriority = priority === GqlTaskPriority.CRITICAL || priority === GqlTaskPriority.HIGH;

  const tooltipContent = (
    <Stack gap={2}>
      <Text size="xs" fw={600}>{eventInfo.event.title}</Text>
      {status && <Text size="xs">Status: {(status as string).replace(/_/g, ' ')}</Text>}
      {priority && <Text size="xs">Priority: {(priority as string).replace(/_/g, ' ')}</Text>}
      {dueAt && <Text size="xs" c="red.4">Due: {new Date(dueAt as string).toLocaleString()}</Text>}
      {description && <Text size="xs" c="dimmed" lineClamp={2}>{description as string}</Text>}
      <Text size="xs" c="dimmed">{eventInfo.timeText}</Text>
    </Stack>
  );

  return (
    <Tooltip label={tooltipContent} multiline w={220} withArrow position="top">
      <Box
        px={4}
        py={1}
        style={{
          fontSize: 11,
          lineHeight: 1.3,
          overflow: 'hidden',
          borderLeft: priorityColor ? `3px solid ${priorityColor as string}` : undefined,
          paddingLeft: priorityColor ? 4 : undefined,
        }}
      >
        <div style={{
          fontWeight: isHighPriority ? 700 : 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {eventInfo.event.title}
        </div>
        {eventInfo.view.type !== 'dayGridMonth' && (
          <div style={{ opacity: 0.85, fontSize: 10 }}>
            {eventInfo.timeText}
            {status && ` \u00B7 ${(status as string).replace(/_/g, ' ').toLowerCase()}`}
          </div>
        )}
        {progressPercent != null && (progressPercent as number) > 0 && (
          <div style={{
            height: 2,
            backgroundColor: 'rgba(255,255,255,0.3)',
            borderRadius: 1,
            marginTop: 2,
          }}>
            <div style={{
              height: '100%',
              width: `${progressPercent as number}%`,
              backgroundColor: '#fff',
              borderRadius: 1,
            }} />
          </div>
        )}
      </Box>
    </Tooltip>
  );
}
